import React, { Dispatch, useEffect, useState } from 'react';
import { navigateTo } from '../../routing/Navigation';
import { Card, Breadcrumbs, BreadcrumbSegment, Button, TextInput } from '@orderly/morrisons-component-library';
import QueryString from 'query-string';
import Table from '../../components/Table/Table';
import { connect } from 'react-redux';
import './Printshop.scss';
import { IStoreState, IDocument, IDocumentBlob, FolderType } from '../../types';
import { IGetFolderContentsParams } from '../../api/Documents/getFolderContentsApi';
import { getFolderContentsRequest, clearFolderContents } from '../../actions/Documents/folderContentsAction';
import { IDownloadDocumentBlobParams } from '../../api/Documents/downloadDocumentBlobApi';
import { downloadDocumentBlobRequest } from '../../actions/Documents/downloadDocumentBlobAction';
import { getFileIcon } from '../../helpers/Documents/DocumentUploadHelper';
import { IPrintshopProps } from './IPrintshopProps';
import { IPopupPropsConfigContentAction } from '../../components/popup/interfaces/IPopupPropsConfigContentAction';
import LoadingThrobber from '../../components/LoadingThrobber/LoadingThrobber';
import { IUserAcceptanceParams } from '../../api/Reports/ConfirmedOrders/IUserAcceptanceParams';
import {
  getUserAcceptanceReportRequest,
  resetUserAcceptanceReportData,
} from '../../actions/Reports/userAcceptanceAction';
import { GenerateDownload } from '../../helpers/DownloadHelper';
import { formatShortDateString } from '../../helpers/Format';

const Printshop = (props: IPrintshopProps) => {
  const [search, setSearch] = useState<string>('');

  const {
    getFolderContents,
    userAcceptanceCsvDownload,
    folderContents,
    userDetails,
    downloadDocument,
    downloadUserAcceptanceCsv,
    clearUserAcceptanceCsvDownload,
  } = props;

  const { data, loading } = folderContents;
  const { folders, documents } = data;

  const loadData = () => {
    const queryString = QueryString.parse(window.location.search);
    const folderPath = queryString.folder as string || '';
    getFolderContents(
      {
        folderPath,
        retailerId: userDetails.retailerId,
        prefix: '',
        folderType: FolderType.Printshop,
      });
  };

  const getFolderName = () => {
    const queryString = QueryString.parse(window.location.search);
    const folderPath =  queryString.folder as string || '';
    const folders = folderPath.split('/');
    return folders[folders.length - 1];
  };

  const getFilteredDocuments = (search: string) => {
    let filteredDocs: IDocument[] = [];

    if (documents.length > 0) {
      filteredDocs = documents.filter((document: IDocument) => {
        return document.description.toLowerCase().indexOf(search.toLowerCase()) > -1;
      });
    }

    return filteredDocs;
  };

  useEffect(() => {
    clearFolderContents();
    loadData();
  }, []);

  useEffect(() => {
    if (userAcceptanceCsvDownload.data) {
      const { filename, content } = userAcceptanceCsvDownload.data;
      generateDownload(filename, content);
    }
  }, [userAcceptanceCsvDownload]);

  useEffect(() => {
    getFilteredDocuments(search);
  }, [search]);

  useEffect(() => {
    if (folderContents.error) {
      navigateTo('/printshop');
      loadData();
    }
  }, [folderContents.error]);

  const folderIsPresent = () => {
    const folder = QueryString.parse(window.location.search).folder;
    return folder !== undefined && folder !== '';
  };

  const getBreadcrumbs = () => {
    const queryString = QueryString.parse(window.location.search);
    const folderPath = queryString.folder as string;

    const breadcrumbs: BreadcrumbSegment[] = [
      { key: 0, text: 'Home', url: '/home/' },
      { key: 1, text: 'Printshop', url: '/printshop' },
    ];

    if (folderPath) {
      const folderParts = folderPath.split('/');

      for (let i = 0; i < folderParts.length; i++) {
        let url = `/printshop?folder=${folderParts[0]}`;
        for (let j = 1; j <= i; j++) {
          url = `${url}/${folderParts[j]}`;
        }
        breadcrumbs.push({ url, key: i + 3, text: folderParts[i] });
      }
    }

    return breadcrumbs;
  };

  const navigateToFolder = (folder: string) => {
    let folderParam = folder;
    if (folderIsPresent()) {
      folderParam = `${QueryString.parse(window.location.search).folder}/${folder}`;
    }

    navigateTo(`/printshop?folder=${folderParam}`);
    loadData();
  };

  const downloadFile = (fileId: number) => {
    downloadDocument(
      {
        retailerId: userDetails.retailerId,
        documentId: fileId,
        prefix: '',
        userAcceptance: false,
        folderType: FolderType.Printshop,
      },
      (data: IDocumentBlob) => {
        generateDownload(data.fileName, data.data);
      },
      () => {
        alert('Failure');
      });
  };

  const downloadUserAcceptanceReport = async (documentId: number) => {
    await downloadUserAcceptanceCsv(
      {
        documentId,
        retailerId: userDetails.retailerId,
      });
  };

  const generateDownload = (filename: string, contents: string) => {
    GenerateDownload(contents, filename);
    clearUserAcceptanceCsvDownload();
  };

  const setPopupConfig = (document: IDocument) => {
    return {
      content: {
        actions: [
          ...(document.requiresAcceptance
            ? [{
              onClick: async (): Promise<void> => {
                await downloadUserAcceptanceReport(document.documentId);
              },
              label: {
                icon: 'stats-bars',
                text: 'User Acceptance Report',
              },
            }]
            : []),
          {
            onClick: (): void => {
              downloadFile(document.documentId);
            },
            label: {
              icon: '',
              text: 'Download',
            },
          },
        ],
      },
    };
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="printshop-container">
      <Breadcrumbs
        className="printshop-breadCrumbs"
        onHomeClick={() => navigateTo('/home')}
        onRedirect={(route: string) => {
          navigateTo(route);
          loadData();
        }}
        segments={breadcrumbs}
      />
      <section className="printshop-heading-section">
        <h1 className="printshop-heading">PRINTSHOP</h1>
        <div className="printshop-folder-name">{getFolderName()}</div>
      </section>
      {
        loading && <LoadingThrobber />
      }
      <div className="printshop-content-container">
        <section className="printshop-filters-section">
          <Button
            text="Create POS file"
            type="button"
            className="btn btn-success create-pos-file-btn"
            onClick={() => navigateTo('/pos-creator')}
          />
          <Card className="printshop-card">
            <h3 className="printshop-filters-heading printshop-filter-by-heading">FILTER</h3>
            <TextInput
              className="search"
              name="documentSearch"
              type="text"
              label="Filter by name"
              value={search}
              onChange={(e: any) => setSearch(e)}
            />
          </Card>
          {
            folderContents.data && folderContents.data.expiryDate &&
            <Card className="printshop-card">
              <h3 className="printshop-filters-heading printshop-filter-by-heading">FOLDER EXPIRY DATE</h3>
              <div className="printshop-folder-expiry">{formatShortDateString(folderContents.data.expiryDate)}</div>
            </Card>
          }
        </section>
        <div className="printshop-folders-contents-container">
          {
            folders.length > 0 &&
            <Card className="printshop-card printshop-base-folders-content">
              {
                folders.filter(x =>
                  x.description.toLowerCase().indexOf(search.toLowerCase()) > -1)
                  .map((baseFolder) => {
                    return (
                      <div
                        title="Click to browse"
                        key={baseFolder.folderId}
                        className="base-folder"
                        onClick={() => navigateToFolder(baseFolder.description)}
                      >
                        <span className={'icon icon-folder'} />
                        <span className="base-folder-description">{baseFolder.description}</span>
                      </div>
                    );
                  })
              }
            </Card>
          }
          {
            getFilteredDocuments(search) &&
            getFilteredDocuments(search).length > 0 &&
            <Table className="printshop-list">
              <thead>
                <tr>
                  <th className="Table--sticky-column">Document Name</th>
                  <th className="Table--fixed-column">Last Modified</th>
                  <th className="Table--fixed-column">Owner</th>
                  <th className="Table--fixed-column table-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredDocuments(search)
                  .map((document: IDocument) => {
                    return (
                      <tr key={document.documentId}>
                        <td className="Table--sticky-column">
                          <span className={`icon ${getFileIcon(document.mimeType).className}`} />{document.description}
                        </td>
                        <td>{new Date(document.lastEditDate).toLocaleDateString()}</td>
                        <td>
                          {document.createdByUser &&
                            `${document.createdByUser.firstname} ${document.createdByUser.lastname}`}
                        </td>
                        <td className="Table--actions">
                          {
                            setPopupConfig(document).content.actions
                              .map((action: IPopupPropsConfigContentAction, index: number): JSX.Element => {
                                return (
                                  <div className="table-buttons" key={index} >
                                    <button
                                      className="Popup-button"
                                      name="popupButton"
                                      onClick={
                                        (): void => {
                                          action.onClick();
                                        }
                                      }
                                    >
                                      {<span
                                        className={`Popup-button-icon icon-${action.label.icon || action.label.text}`}
                                        title={action.label.text}
                                      />
                                      }
                                    </button>
                                  </div>
                                );
                              },
                              )
                          }
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </Table>
          }
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
    folderContents: state.documentsFolderContents,
    downloadedDocument: state.documentsDownloadBlob,
    userDetails: state.userDetails,
    userAcceptanceCsvDownload: state.userAcceptance,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    getFolderContents: (parameters: IGetFolderContentsParams) => dispatch(getFolderContentsRequest(parameters)),
    downloadDocument: (parameters: IDownloadDocumentBlobParams, onSuccess: (data: IDocumentBlob) => void,
      onFailure: () => void) => dispatch(downloadDocumentBlobRequest(parameters, onSuccess, onFailure)),
    downloadUserAcceptanceCsv: (parameters: IUserAcceptanceParams) => dispatch(getUserAcceptanceReportRequest(parameters)),
    clearUserAcceptanceCsvDownload: () => dispatch(resetUserAcceptanceReportData()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Printshop);
